import { memo } from 'react'
import { AnimatePresence, motion, Variants, Transition } from 'framer-motion'
import { ChevronsDown } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'

const variants: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
}

const transition: Transition = { duration: 0.15, ease: 'easeOut' }

interface ScrollButtonProps {
  show: boolean
  onClick: () => void
}

/**
 * Jump-to-latest for a view's scrollable body, rendered at the leading edge of
 * the floating bar. A floating `Button` like the notepad beside it, so it
 * carries the row's size, surface and press feedback rather than its own.
 *
 * Always clickable: both hosts float it over a `pointer-events-none` layer, and
 * a jump-to-latest that can't be pressed is just a picture.
 */
export const ScrollButton = memo(({ show, onClick }: ScrollButtonProps) => {
  return (
    // anchorY="bottom", not the default top: both hosts size to this row and
    // pin it by its bottom edge, so when this button is the row's only in-flow
    // child, popping it collapses the row and a captured `top` resolves against
    // an edge that just moved — the exiting circle would drop by its own height
    // to fade. Its bottom edge is the one that holds still.
    <AnimatePresence initial={false} mode="popLayout" anchorY="bottom">
      {show && (
        <motion.div
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={transition}
          className="pointer-events-auto shrink-0"
        >
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  variant="outline"
                  shape="square"
                  className={FLOATING_FILL_CLASS}
                  aria-label="Jump to latest"
                  onClick={(e) => {
                    e.stopPropagation()
                    onClick()
                  }}
                >
                  <ChevronsDown aria-hidden="true" />
                </Button>
              }
            />
            <Tooltip.Content>Jump to latest</Tooltip.Content>
          </Tooltip>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

ScrollButton.displayName = 'ScrollButton'
